import { AssetType } from "@/constants/enums";
import { OrganizationType, OrganizationTypeValue } from "@/types/domain";
import { typeNavLink, UserType, UserRole } from "@/types";
import type { CurrentUser } from "@/types/api/user";
import { BanknotesIcon, BuildingStorefrontIcon, ChartBarIcon, CommandLineIcon, ComputerDesktopIcon, CurrencyEuroIcon, LinkIcon, Square3Stack3DIcon } from "@heroicons/react/24/outline";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

type Props = {
  user: CurrentUser,
  entityType: OrganizationTypeValue | undefined,
  sideMenuOpen: boolean,
  assetType: AssetType | undefined,
  setSideMenuOpen: Dispatch<SetStateAction<boolean>>
  disabled?: boolean
}

function Navlinks(props: Props) {
  const { user, sideMenuOpen, entityType, assetType, setSideMenuOpen, disabled = false } = props;

  const { t } = useTranslation(["components"]);

  const navLinks: typeNavLink[] = [
    {name: t("header.navlinks.dashboard"), link: "/user/dashboard", visibility: [UserType.Employee, UserType.Financier], icon: <ComputerDesktopIcon className="mf-icon-base" />},
    {name: t("header.navlinks.makeinvestment"), link: "/user/investment", visibility:[UserType.Financier], icon: <BanknotesIcon className="mf-icon-base" />},
    {name: t("header.navlinks.portfolio"), link: "/user/portfolio", visibility:[UserType.Financier], icon: <ChartBarIcon className="mf-icon-base" />},
    {name: t("header.navlinks.assetsoverview"), link: "/user/assetoverview", visibility: [UserType.Employee, OrganizationType.Merchant], icon: <Square3Stack3DIcon className="mf-icon-base" />},
    {name: t("header.navlinks.adminpage"), link: "/admin/dashboard", visibility: [UserType.Developer], icon: <CommandLineIcon className="mf-icon-base" />},
    // TODO: Customers page disabled
    // {name: t("header.navlinks.customers"), link: "/user/customersoverview", visibility: [OrganizationType.Merchant, AssetType.Receivable], icon: <UserCircleIcon className="mf-icon-base" />},
    {name: t("header.navlinks.financiers"), link: "/admin/financiers", visibility: [UserType.Developer], icon: <CurrencyEuroIcon className="mf-icon-base" />},
    {name: t("header.navlinks.merchants"), link: "/admin/merchants", visibility: [UserType.Developer], icon: <BuildingStorefrontIcon className="mf-icon-base" />},
    {name: t("header.navlinks.tokentransactions"), link: user.userRole === UserRole.Admin ? "/admin/transactions": "/user/transactions", visibility: [UserType.Developer, UserRole.Superuser], icon: <LinkIcon className="mf-icon-base" />},
  ];

  return (
    <div
      className={cn(
        "z-100 md:text-gray-600 dark:text-white bg-white md:bg-transparent dark:bg-mfdarklight flex md:flex-row flex-col absolute md:static md:flex-1 shadow-lg dark:shadow-gray-950 md:shadow-none gap-y-2 md:gap-y-0 px-2 md:px-0 rounded-b-lg md:rounded-b-none md:gap-x-2 pb-6 md:pb-0 md:opacity-100",
        sideMenuOpen ? "top-[90px] left-0 right-0 opacity-100" : "top-[-500px] opacity-0"
      )}
    >
      {
        navLinks.map(({name, link, visibility, icon}) => (
          (
            visibility.includes(user.userRole) ||
            visibility.includes(user.userType) ||
              (!!entityType && visibility.includes(entityType) && visibility.includes(assetType as string))
          ) && (
            disabled ? (
              <span
                key={link + name}
                aria-disabled="true"
                className="flex items-center p-4 md:p-2 rounded-md relative text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50"
              >
                <div className="md:hidden lg:flex">
                  {icon}
                </div>
                <div className="flex font-semibold">
                  {name}
                </div>
              </span>
            ) : (
              <Link
                key={link + name}
                to={link}
                activeOptions={{ exact: true }}
                onClick={() => setSideMenuOpen(false)}
                className="flex items-center p-4 md:p-2 rounded-md relative text-gray-800 dark:text-gray-100 transition-colors duration-200 ease-in-out hover:text-mforange data-[status=active]:text-mforange dark:data-[status=active]:text-mforange"
              >
                <div className="md:hidden lg:flex">
                  {icon}
                </div>
                <div className="group-hover:flex flex font-semibold">
                  {name}
                </div>
              </Link>
            )
          )
        ))
      }
    </div>
  );
}

export default Navlinks;
